import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestSeafoodVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-seafood-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-blue-water-cafe": "Blue Water Cafe",
    "section-2-miku": "Miku",
    "section-3-ancora": "Ancora",
    "section-4-joe-fortes": "Joe Fortes",
    "section-5-sandbar": "The Sandbar",
    "section-6-rodneys": "Rodney’s Oyster House",
    "section-7-salmon-n-bannock": "Salmon n’ Bannock",
    "section-8-fish-counter": "The Fish Counter",
    "section-9-go-fish": "Go Fish",
    "section-10-sustainability": "Sustainability",
    faq: "FAQ",
    "local-regulations": "Local Regulations / Best Practices",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism",
    },
    {
      url: "https://www.ocean.org/seafood",
      label: "Ocean Wise Sustainable Seafood Program",
    },
    {
      url: "https://www.pac.dfo-mpo.gc.ca",
      label: "Fisheries and Oceans Canada (Pacific Region)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed; each "id" matches an h2 or p ref
  const locations = [
    {
      id: "section-1-blue-water-cafe",
      lat: 49.2761,
      lng: -123.1209,
      popupText: "Blue Water Cafe + Raw Bar",
    },
    {
      id: "section-2-miku",
      lat: 49.2869,
      lng: -123.1128,
      popupText: "Miku Restaurant",
    },
    {
      id: "section-3-ancora",
      lat: 49.2728,
      lng: -123.1345,
      popupText: "Ancora Waterfront Dining & Patio",
    },
    {
      id: "section-4-joe-fortes",
      lat: 49.2849,
      lng: -123.1247,
      popupText: "Joe Fortes Seafood & Chop House",
    },
    {
      id: "section-5-sandbar",
      lat: 49.2713,
      lng: -123.1339,
      popupText: "The Sandbar Seafood Restaurant",
    },
    {
      id: "section-6-rodneys",
      lat: 49.2743,
      lng: -123.1233,
      popupText: "Rodney’s Oyster House",
    },
    {
      id: "section-7-salmon-n-bannock",
      lat: 49.2633,
      lng: -123.1298,
      popupText: "Salmon n’ Bannock Bistro",
    },
    {
      id: "section-8-fish-counter",
      lat: 49.2485,
      lng: -123.1010,
      popupText: "The Fish Counter",
    },
    {
      id: "section-9-go-fish",
      lat: 49.2702,
      lng: -123.1340,
      popupText: "Go Fish Ocean Emporium",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Seafood Restaurants in Vancouver</title>
        <meta
          name="description"
          content="A detailed guide to the top seafood restaurants in Vancouver, from fine dining to casual spots."
        />
        <meta
          name="keywords"
          content="seafood, Vancouver, restaurants, dining, Ocean Wise, sustainable, fish"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-seafood-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Seafood Restaurants in Vancouver"
        />
        <meta
          property="og:description"
          content="From upscale dining to casual fish shacks, explore Vancouver's top seafood spots."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-seafood-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-seafood-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Seafood Restaurants in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Check out the best places for a seafood feast in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-seafood-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="A plate of assorted seafood in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s coastal location and vibrant dining scene make it a
              seafood lover’s paradise. From high-end restaurants showcasing
              impeccably fresh fish to laid-back fish shacks serving crispy
              fish & chips, the city offers an array of ocean delights for every
              taste and budget. In this guide, we’ll explore Vancouver’s top
              seafood spots, including their specialties, price ranges, and
              sustainability practices like Ocean Wise certification.
            </p>

            {/* SECTION 1: Blue Water Cafe */}
            <h2 id="section-1-blue-water-cafe">Blue Water Cafe + Raw Bar</h2>
            <p
              ref={(el) => setSpotRef("section-1-blue-water-cafe", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1095 Hamilton Street, Vancouver, BC |
              <Marker
                coordY={49.2761}
                coordX={-123.1209}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2761, Longitude: -123.1209)
            </p>
            <p>
              Blue Water Cafe is among Vancouver’s most renowned seafood fine
              dining establishments. Housed in a refurbished warehouse in
              Yaletown, it features a refined yet lively ambiance, open kitchen,
              and an extensive raw bar. Expect creative, high-quality dishes
              like BC sablefish, fresh oysters, and expertly crafted sushi. The
              restaurant is proud of its Ocean Wise partnership, ensuring
              sustainable and local sourcing whenever possible. Although prices
              lean towards $$$–$$$$, it’s a must-try for special occasions.
            </p>

            {/* SECTION 2: Miku */}
            <h2 id="section-2-miku">Miku Restaurant</h2>
            <p
              ref={(el) => setSpotRef("section-2-miku", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> #70-200 Granville Street, Vancouver, BC
              |
              <Marker
                coordY={49.2869}
                coordX={-123.1128}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2869, Longitude: -123.1128)
            </p>
            <p>
              Miku is known for introducing Aburi (flame-seared) sushi to
              Vancouver and boasts spectacular views overlooking the Burrard
              Inlet. The menu focuses on high-quality seafood, including sushi,
              sashimi, and various fusion dishes, all created with a
              sustainability mindset. With an upscale yet approachable vibe,
              Miku also accommodates dietary needs by offering vegetarian rolls
              and non-seafood items. Prices are on the higher side, but the
              waterfront setting and innovative cuisine are worth it.
            </p>

            {/* SECTION 3: Ancora */}
            <h2 id="section-3-ancora">Ancora Waterfront Dining & Patio</h2>
            <p
              ref={(el) => setSpotRef("section-3-ancora", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1600 Howe Street, Vancouver, BC |
              <Marker
                coordY={49.2728}
                coordX={-123.1345}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2728, Longitude: -123.1345)
            </p>
            <p>
              Overlooking False Creek, Ancora seamlessly blends Peruvian and
              Japanese cuisines in a style often referred to as “Nikkei.” The
              seafood-forward menu features dishes like ceviche with local BC
              shellfish, sushi rolls with Latin-inspired sauces, and grilled
              sablefish. Enjoy the elegant waterfront patio for a romantic
              dinner or scenic brunch. Ancora also supports sustainable fishing
              practices and offers vegetarian options. Expect a $$$ price tag,
              but the inventive flavors and stunning views deliver a unique
              dining experience.
            </p>

            {/* SECTION 4: Joe Fortes */}
            <h2 id="section-4-joe-fortes">Joe Fortes Seafood & Chop House</h2>
            <p
              ref={(el) => setSpotRef("section-4-joe-fortes", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 777 Thurlow Street, Vancouver, BC |
              <Marker
                coordY={49.2849}
                coordX={-123.1247}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2849, Longitude: -123.1247)
            </p>
            <p>
              A classic dining institution, Joe Fortes combines old-school charm
              with a wide selection of premium seafood and steak options.
              Popular dishes include fresh oysters, lobster rolls, and cedar
              plank salmon. While it’s upscale enough for special occasions
              (especially at dinner), the restaurant also offers a bustling
              lunch scene and happy hour deals. Joe Fortes’ multi-level setting
              features an expansive rooftop patio and live piano music, making
              it both lively and family-friendly.
            </p>

            {/* SECTION 5: The Sandbar */}
            <h2 id="section-5-sandbar">The Sandbar Seafood Restaurant</h2>
            <p
              ref={(el) => setSpotRef("section-5-sandbar", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1535 Johnston Street, Granville Island,
              Vancouver, BC |
              <Marker
                coordY={49.2713}
                coordX={-123.1339}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2713, Longitude: -123.1339)
            </p>
            <p>
              Situated over the water at Granville Island, The Sandbar offers
              fresh seafood in a warm, casual atmosphere. With an oyster bar, a
              sushi bar, and a wood-burning grill, the menu caters to a broad
              range of tastes, from fish & chips to cedar plank salmon and
              seafood pastas. Guests can enjoy live music in the lounge, while
              the patio area boasts scenic views of False Creek and the downtown
              skyline. The Sandbar is an excellent spot to combine a meal with a
              day of exploring Granville Island’s markets and shops.
            </p>

            {/* SECTION 6: Rodney’s Oyster House */}
            <h2 id="section-6-rodneys">Rodney’s Oyster House</h2>
            <p
              ref={(el) => setSpotRef("section-6-rodneys", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1228 Hamilton Street, Vancouver, BC |
              <Marker
                coordY={49.2743}
                coordX={-123.1233}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2743, Longitude: -123.1233)
            </p>
            <p>
              Rodney’s is a casual, lively oyster bar in Yaletown that offers
              one of the widest selections of oysters in the city, along with
              chowders, mussels, and crab cakes. The staff are friendly and
              engaging, often sharing insider oyster knowledge. “Low Tide”
              happy hour (roughly 2:00 PM–6:00 PM) is a local favorite for
              discounted oysters and drinks. Rodney’s keeps the atmosphere fun
              and unpretentious—perfect for an informal get-together.
            </p>

            {/* SECTION 7: Salmon n’ Bannock */}
            <h2 id="section-7-salmon-n-bannock">Salmon n’ Bannock Bistro</h2>
            <p
              ref={(el) => setSpotRef("section-7-salmon-n-bannock", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> #7-1128 West Broadway, Vancouver, BC |
              <Marker
                coordY={49.2633}
                coordX={-123.1298}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2633, Longitude: -123.1298)
            </p>
            <p>
              Salmon n’ Bannock is Vancouver’s only Indigenous-owned and
              -operated restaurant, featuring modern Indigenous cuisine with
              wild BC salmon at its core. Experience dishes like cedar-plank
              sockeye salmon, candied salmon, or hearty chowders—all served with
              the restaurant’s signature bannock (a traditional fried bread).
              Sustainability and respect for the land and sea are central to
              their menu. Although the bistro is small, it offers an intimate,
              welcoming dining atmosphere that honors First Nations traditions.
            </p>

            {/* SECTION 8: The Fish Counter */}
            <h2 id="section-8-fish-counter">The Fish Counter</h2>
            <p
              ref={(el) => setSpotRef("section-8-fish-counter", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 3825 Main Street, Vancouver, BC |
              <Marker
                coordY={49.2485}
                coordX={-123.1010}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2485, Longitude: -123.1010)
            </p>
            <p>
              For a casual, budget-friendly option that champions sustainability,
              look no further than The Fish Counter. Part seafood market, part
              café, this spot was founded by individuals closely involved with
              Vancouver’s Ocean Wise program. Their fish & chips (wild salmon,
              Pacific cod) are standout favorites, and they also serve tasty
              chowders and fish tacos. Seating is limited, so many patrons opt
              for takeout. It’s a local go-to for fresh, responsibly sourced
              seafood that won’t break the bank.
            </p>

            {/* SECTION 9: Go Fish Ocean Emporium */}
            <h2 id="section-9-go-fish">Go Fish Ocean Emporium</h2>
            <p
              ref={(el) => setSpotRef("section-9-go-fish", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 1505 West 1st Avenue, near Fishermen’s
              Wharf |{" "}
              <Marker
                coordY={49.2702}
                coordX={-123.1340}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2702, Longitude: -123.1340)
            </p>
            <p>
              Situated near Granville Island’s Fishermen’s Wharf, Go Fish is a
              tiny outdoor stand beloved for its fresh-off-the-boat fish &
              chips, salmon tacones, and seasonal catches. With only a few
              picnic tables and a picturesque view of the water, it offers an
              authentically local experience. Lines can get long on sunny days,
              but the wait is worth it for the ocean-fresh flavors. Go Fish also
              focuses on Ocean Wise sourcing, underscoring Vancouver’s
              commitment to sustainable fisheries.
            </p>

            {/* SECTION 10: Sustainability */}
            <h2 id="section-10-sustainability">Sustainability in Vancouver Seafood</h2>
            <p>
              Vancouver prides itself on being a leader in sustainable seafood,
              largely thanks to the Ocean Wise program. Many restaurants here
              highlight local, seasonal catches such as spot prawns in late
              spring or wild Pacific salmon in the summer. Local regulations
              also ensure that shellfish is sourced responsibly to avoid red
              tide issues. Look for the Ocean Wise symbol on menus to make
              eco-friendly choices and support restaurants that prioritize
              healthy oceans.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is Ocean Wise?</h3>
              <p>
                Ocean Wise is a program that certifies restaurants and fish
                markets serving sustainably sourced seafood. Look for the Ocean
                Wise symbol on menus to ensure you’re making environmentally
                responsible choices.
              </p>

              <h3>2. Do I need reservations at these restaurants?</h3>
              <p>
                Reservations are highly recommended for fine dining or popular
                spots like Blue Water Cafe, Miku, or Ancora. More casual places
                (Rodney’s, The Fish Counter, Go Fish) typically operate on a
                first-come-first-served basis.
              </p>

              <h3>3. Are there vegetarian options at seafood restaurants?</h3>
              <p>
                Yes. Many spots offer salads, vegetarian sushi rolls, or
                plant-based sides. Some restaurants like Ancora even have
                dedicated vegetarian entrées. It’s always best to check menus or
                call ahead to confirm.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              Vancouver follows strict guidelines around safe seafood handling
              and sourcing. Many restaurants voluntarily adhere to the Ocean
              Wise program. Shellfish must be purchased from approved suppliers
              to reduce the risk of red tide contamination. Always heed any
              posted advisories about consuming raw oysters, particularly during
              the warmer summer months.
            </p>
            <p>
              <strong>Disclaimer:</strong> Always verify current health and
              safety updates from official government resources and follow any
              local advisories regarding seafood consumption.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s seafood scene truly offers something for everyone,
              from polished fine dining experiences to the simplicity of
              takeout fish & chips enjoyed by the water. By choosing Ocean Wise
              and seasonal offerings, diners can indulge in Vancouver’s ocean
              bounty responsibly. Whether you’re a devoted seafood enthusiast or
              just exploring new flavors, these restaurants capture the essence
              of a city intimately connected to the sea. Bon appétit!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}